import React, { useState } from "react";
import { Link } from "react-router-dom";
import ContactUsCard from "../ContactUsCard";

export default function ProductPageRedesign({ title, description, items, bannerImage }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [expandedCard, setExpandedCard] = useState("");

  const handleCardToggle = (productId) => {
    if (expandedCard === productId) {
      setExpandedCard("");
    } else {
      setExpandedCard(productId);
    }
  };

  // Filter items based on search and status tabs
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.rLDProduct_Dev.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.remark && item.remark.toLowerCase().includes(searchTerm.toLowerCase()));

    const isCompleted = item.technologyTransfer === "Done";
    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Completed" && isCompleted) ||
      (statusFilter === "In Progress" && !isCompleted);

    return matchesSearch && matchesStatus;
  });

  // Stepper Node renderer helper
  const renderStepNode = (label, value, isDevelopmentStep = false) => {
    let status = "gray"; // completed | active | pending | gray
    
    if (value === "Done") {
      status = "completed";
    } else if (isDevelopmentStep && value !== "Done") {
      status = "active";
    } else if (value === "") {
      status = "pending";
    }

    const configs = {
      completed: {
        bg: "bg-brand-teal text-white",
        border: "border-brand-teal",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
        ),
      },
      active: {
        bg: "bg-brand-blue text-white ring-4 ring-brand-blue/20 animate-pulse",
        border: "border-brand-blue",
        icon: (
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H17" />
          </svg>
        ),
      },
      pending: {
        bg: "bg-amber-50 text-amber-600 border border-amber-300",
        border: "border-amber-300",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ),
      },
      gray: {
        bg: "bg-slate-100 text-slate-400 border border-slate-200",
        border: "border-slate-200",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
      },
    };

    const current = configs[status];

    return (
      <div className="flex flex-col items-center relative flex-1">
        {/* Node Circle */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold z-10 transition-all ${current.bg}`}>
          {current.icon}
        </div>
        {/* Label */}
        <span className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mt-1 text-center">
          {label}
        </span>
        {/* Detail status badge */}
        <span className={`text-[9px] font-bold uppercase mt-0.5 px-1.5 py-0.25 rounded-full border ${
          status === "completed" ? "bg-green-50 text-green-700 border-green-200" :
          status === "active" ? "bg-blue-50 text-blue-700 border-blue-200" :
          status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
          "bg-slate-50 text-slate-400 border-slate-200"
        }`}>
          {status === "completed" ? "Done" :
           status === "active" ? "Active" :
           status === "pending" ? "Planned" : "N/A"}
        </span>
      </div>
    );
  };

  const regionNames = ["USA", "EU", "China", "Australia", "S.E. Asia", "Other"];

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* 1. Page Hero Section */}
      <section className="relative min-h-[35vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25 mix-blend-overlay"
          style={{
            backgroundImage: `url('${bannerImage || "https://images.unsplash.com/photo-1576086213369-97a306d36557?q=80&w=1920&auto=format&fit=crop"}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Product Portfolio
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            {title}
          </h1>
          <p className="max-w-3xl mx-auto text-sm md:text-base text-slate-300 font-light leading-relaxed">
            {description}
          </p>
        </div>
      </section>

      {/* 2. Interactive Search & Filtering Section */}
      <section className="py-12 max-w-5xl mx-auto px-6">
        <div className="bg-white border border-slate-100 p-4 md:p-6 rounded-2xl shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
          
          {/* Live Search Input */}
          <div className="relative w-full md:w-72">
            <input
              type="text"
              placeholder="Search product or RLD..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
            />
            <svg className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                Clear
              </button>
            )}
          </div>

          {/* Filtering Tab Controls */}
          <div className="flex bg-slate-100 p-1 rounded-xl w-full md:w-auto">
            {["All", "Completed", "In Progress"].map((tab) => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  statusFilter === tab
                    ? "bg-white text-brand-blue shadow-sm"
                    : "text-slate-500 hover:text-brand-blue"
                }`}
              >
                {tab === "All" ? "All Products" : tab}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 max-w-5xl mx-auto px-6">
        <div className="space-y-4">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, idx) => {
              const isExpanded = expandedCard === `prod${item.productID}`;
              return (
                <div
                  key={idx}
                  className={`bg-white border rounded-2xl p-4 md:p-5 shadow-sm hover:shadow-md transition-all duration-300 ${
                    isExpanded 
                      ? "border-brand-teal/40 ring-1 ring-brand-teal/20" 
                      : "border-slate-100 hover:border-slate-200"
                  }`}
                >
                  
                  {/* Card Header & Stepper Grid */}
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="space-y-1 text-left">
                        <h3 className="text-xl font-bold text-brand-blue tracking-tight leading-tight">
                          {item.productName}
                        </h3>
                        <p className="text-xs text-slate-400 font-light">
                          RLD Reference: <span className="font-semibold text-slate-500">{item.rLDProduct_Dev}</span>
                        </p>
                      </div>

                      {/* Reach For Licensing action directly in header */}
                      <div className="flex items-center space-x-2.5">
                        <button
                          onClick={() => handleCardToggle(`prod${item.productID}`)}
                          className={`inline-flex items-center space-x-2 text-xs font-semibold px-4 py-2 rounded-xl border transition-all ${
                            isExpanded
                              ? "bg-slate-100 text-slate-800 border-slate-200"
                              : "bg-white text-brand-teal hover:text-brand-teal-dark border-slate-200 hover:border-brand-teal/30 hover:bg-slate-50"
                          }`}
                        >
                          <span>{isExpanded ? "Hide Regions" : "View Regions"}</span>
                          <svg
                            className={`w-3 h-3 transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Stepper Track */}
                    <div className="relative pt-3 pb-1 border-t border-b border-slate-50">
                      {/* Desktop connecting line */}
                      <div className="hidden md:block absolute left-[10%] right-[10%] top-[28px] h-[2px] bg-slate-200 -z-0" />
                      
                       <div className="grid grid-cols-3 sm:grid-cols-5 md:flex md:flex-row gap-y-6 gap-x-2 md:gap-0 justify-between items-stretch md:items-center relative z-10">
                        {renderStepNode("Development", item.productDevStatus, true)}
                        {renderStepNode("Pilot BE", item.pilotBE)}
                        {renderStepNode("Scale Up", item.scaleUp)}
                        {renderStepNode("Pivotal BE", item.pivotalBE)}
                        {renderStepNode("Dossier", item.technologyTransfer)}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Section: Licensing Table and Action */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-slate-100 space-y-6 animate-fadeIn">
                      
                      {/* Remarks Callout */}
                      {item.remark && (
                        <div className="bg-brand-blue/5 border-l-4 border-brand-blue p-4 rounded-r-xl text-left">
                          <p className="text-xs font-bold text-brand-blue uppercase tracking-wider">Scientific Remarks</p>
                          <p className="text-sm text-slate-600 font-light mt-0.5 leading-relaxed italic">
                            "{item.remark}"
                          </p>
                        </div>
                      )}

                      {/* Out-licensing status grid */}
                      <div className="space-y-3 text-left">
                        <h4 className="text-xs font-bold text-brand-blue uppercase tracking-wider">
                          Out-Licensing Availability Status
                        </h4>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                          {regionNames.map((regionName, regionIdx) => {
                            const isAvailable = item.avaliableIn && item.avaliableIn[regionIdx] ? item.avaliableIn[regionIdx].value : false;
                            return (
                              <div 
                                key={regionIdx}
                                className={`border p-3 rounded-xl flex flex-col justify-between space-y-1 transition-all ${
                                  isAvailable
                                    ? "bg-emerald-50/30 border-emerald-100 hover:border-emerald-200"
                                    : "bg-slate-50/50 border-slate-200/60"
                                }`}
                              >
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{regionName}</span>
                                <div className="flex items-center space-x-1.5 mt-1">
                                  <span className={`w-1.5 h-1.5 rounded-full ${isAvailable ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
                                  <span className={`text-[10px] font-bold uppercase ${
                                    isAvailable ? "text-emerald-700" : "text-slate-400"
                                  }`}>
                                    {isAvailable ? "Available" : "Licensed"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Footer Licensing Apply Row */}
                      <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-xs text-slate-400 font-light text-left">
                          If you are interested in commercializing or licensing this product in any of the available regions, contact our Business Development desk.
                        </p>
                        
                        <Link
                          to="/ContactUs"
                          state={{ from: `Licensing Inquiry: ${item.productName}` }}
                          className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-brand-teal hover:bg-brand-teal-dark text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm transition-all transform hover:-translate-y-0.5"
                        >
                          <span>Reach For Licensing</span>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </Link>
                      </div>

                    </div>
                  )}

                </div>
              );
            })
          ) : (
            <div className="text-center bg-white border border-slate-100 rounded-3xl p-16">
              <p className="text-slate-500 font-light">No products match your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* 4. Global Contact Us Card Accent */}
      <ContactUsCard sender="Genr" />
    </div>
  );
}
